import React from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
// material-ui components
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { withRights } from "providers/RightsConsumer";
import { Button } from "@totalsoft/rocket-ui";
import { TextField } from "@totalsoft/rocket-ui";
import { defaultNumericPlaces, defaultDecimalPlaces } from "constants/numericLimits";
import PartnerExtraInformationWrapper from "./PartnerExtraInformationWrapper";

export const PartnerExtraInformationDataRowComponent = ({
  section,
  sectionIdx,
  onPropertyChange,
  hasWritePermission,
  onAddExtraInfo,
  onDeleteRow,
  countryCode,
  loadPropertyDetails,
  onSavePartnerExtraInfos,
  saveDisabled,
  validateExtraInformation,
  savedWithMandatoryPropertiesEmpty,
  t,
  saving
}) => {
  const isGridDisplay = section.display === "Grid";
  const isTextArea = section.textArea === true;

  return (
    <Grid container spacing={2} key={sectionIdx}>
      {isGridDisplay ? (
        <Grid container spacing={2}>
          {section?.values?.map((item, index) => (
            <React.Fragment key={index}>
              {section?.columns?.map((column, columnIdx) =>
                isTextArea ? (
                  <Grid container spacing={2} alignItems="center" key={`${index}_${columnIdx}`}>
                    <Grid item xs={4}>
                      <label
                        htmlFor={`extraInfo_${index}_${column.propertyName}`}
                        style={{
                          color: "black",
                          fontSize: "18px",
                          display: "block",
                          marginLeft: "2rem",
                          marginTop: "2rem"
                        }}
                      >
                        {column.required ? `${column.caption}*` : column.caption}
                      </label>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        id={`extraInfo_${index}_${column.propertyName}`}
                        fullWidth
                        variant="outlined"
                        onChange={e => onPropertyChange(index, column.propertyName, e, sectionIdx)}
                        value={item[column.propertyName]}
                        disabled={!hasWritePermission}
                        required={column.required}
                        error={column.required && !item[column.propertyName]}
                        helperText={
                          column.required && !item[column.propertyName] ? t("Validations.Generic.Mandatory") : ""
                        }
                        InputProps={{
                          inputProps: {
                            decimalScale: column.propertyType === "INT" ? defaultNumericPlaces : defaultDecimalPlaces
                          }
                        }}
                        sx={{ marginBottom: 1, marginTop: "2rem" }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={6} xl={4} key={`${index}_${columnIdx}`} sx={{ padding: "0.5rem" }}>
                    <label
                      htmlFor={`extraInfo_${index}_${column.propertyName}`}
                      style={{
                        color: "black",
                        fontSize: "15px",
                        display: "block",
                        marginLeft: "0.5rem"
                      }}
                    >
                      {column.required ? `${column.caption}*` : column.caption}
                    </label>
                    <PartnerExtraInformationWrapper
                      column={column}
                      item={item}
                      index={index}
                      onPropertyChange={onPropertyChange}
                      hasWritePermission={hasWritePermission}
                      countryCode={countryCode}
                      sectionIdx={sectionIdx}
                      t={t}
                      loadPropertyDetails={loadPropertyDetails}
                    />
                  </Grid>
                )
              )}
            </React.Fragment>
          ))}
        </Grid>
      ) : (
        <Grid item xs={12} sm={12} md={12} sx={{ marginTop: "20px", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                {section?.columns?.map((column, index) => (
                  <Tooltip key={index} title={column.caption}>
                    <TableCell key={index}>{column.required ? `${column.caption}*` : column.caption}</TableCell>
                  </Tooltip>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {section?.values?.map((item, index) => (
                <TableRow key={`${index}`}>
                  {section?.columns?.map((column, Column_index) => (
                    <TableCell key={`${index}_${Column_index}`}>
                      <div id={"extra_" + index + "_info"}>
                        <PartnerExtraInformationWrapper
                          column={column}
                          item={item}
                          index={index}
                          onPropertyChange={onPropertyChange}
                          hasWritePermission={hasWritePermission}
                          countryCode={countryCode}
                          sectionIdx={sectionIdx}
                          t={t}
                          loadPropertyDetails={loadPropertyDetails}
                        />
                      </div>
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: "end" }}>
                    <IconButton
                      id={"extraInfo_" + index + "_deleteBtn"}
                      color="primary"
                      variant="text"
                      onClick={() => onDeleteRow(index, sectionIdx)}
                      disabled={!hasWritePermission}
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      )}
      {!isGridDisplay && (
        <Button
          id="extraInfo_addBtn"
          color="primary"
          variant="contained"
          onClick={() => onAddExtraInfo(sectionIdx)}
          disabled={!hasWritePermission}
          sx={{ marginTop: "1.5rem", marginLeft: "1rem" }}
        >
          {t("Add")}
        </Button>
      )}
      <Button
        id="extraInfo_saveBtn"
        color="primary"
        variant="contained"
        onClick={() => onSavePartnerExtraInfos(sectionIdx)}
        disabled={
          saveDisabled[sectionIdx] ||
          !hasWritePermission ||
          (validateExtraInformation([section]) && !savedWithMandatoryPropertiesEmpty)
        }
        sx={{ marginTop: "1.5rem", marginLeft: "1rem" }}
      >
        {saving[sectionIdx] ? t("Saving") : t("Save")}
      </Button>
    </Grid>
  );
};

PartnerExtraInformationDataRowComponent.propTypes = {
  section: PropTypes.shape({
    name: PropTypes.string,
    display: PropTypes.string,
    textArea: PropTypes.bool,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        caption: PropTypes.string.isRequired,
        required: PropTypes.bool.isRequired,
        propertyName: PropTypes.string.isRequired,
        propertyType: PropTypes.string.isRequired,
        propertyCode: PropTypes.string.isRequired
      })
    ),
    values: PropTypes.arrayOf(PropTypes.object)
  }),
  sectionIdx: PropTypes.number,
  onPropertyChange: PropTypes.func,
  onAddExtraInfo: PropTypes.func,
  onDeleteRow: PropTypes.func,
  countryCode: PropTypes.string,
  hasWritePermission: PropTypes.bool,
  loadPropertyDetails: PropTypes.func,
  onSavePartnerExtraInfos: PropTypes.func,
  saveDisabled: PropTypes.bool,
  validateExtraInformation: PropTypes.func,
  savedWithMandatoryPropertiesEmpty: PropTypes.bool,
  t: PropTypes.func.isRequired,
  saving: PropTypes.array.isRequired
};

export default withTranslation()(withRights(PartnerExtraInformationDataRowComponent));
